/**
 * Hosted font packs: the families this deployment publishes beyond the ones
 * the engine bundles, declared at boot the way Studio does it (TraceFrontend
 * `src/lib/fonts/hostFontPacks.ts`, same variable, same manifest).
 *
 * `VITE_FONT_PACK_MANIFEST_URL` names a JSON array of `HostedFontPack`, the
 * shape the engine's `declareFontPacks` takes, sitting beside the atlas files
 * it points at. Unset means this build declares none, and the engine draws its
 * bundled families and nothing else: it ships no pack list by design.
 *
 * Declaring registers the catalogue only (a few KB of URLs). An atlas is
 * fetched when a Text node first names its family, and the paragraph
 * re-measures when it lands.
 */
import { declareFontPacks, type HostedFontPack } from 'trace-viewer/behaviors';

const MANIFEST_URL = import.meta.env?.VITE_FONT_PACK_MANIFEST_URL as string | undefined;
// Match the engine's deadline for fetching a hosted font face.
const FONT_MANIFEST_TIMEOUT_MS = 20_000;

let initialization: Promise<void> | undefined;

/** Test-only: allow another `initHostFontPacks` in the same module instance. */
export function _resetHostFontPacksForTest(): void {
    initialization = undefined;
}

function isHostedFontPack(value: unknown): value is HostedFontPack {
    if (!value || typeof value !== 'object') return false;
    const pack = value as Record<string, unknown>;
    return typeof pack.family === 'string' && Array.isArray(pack.faces);
}

/**
 * Fetch the manifest and declare its packs to the engine, once per page load.
 *
 * Fire-and-forget (`void initHostFontPacks()` in index.ts): text draws through
 * the engine's fallback chain meanwhile, and boot never waits on the network.
 * Every caller receives the same completion promise, so authoring can wait
 * for the catalogue before resolving a style's font names. Never throws.
 * A missing, unreachable or malformed manifest leaves the bundled fonts
 * drawing and warns once; an unset URL silently keeps the bundled catalogue.
 *
 * `url` defaults to this build's `VITE_FONT_PACK_MANIFEST_URL`.
 */
export function initHostFontPacks(url: string | undefined = MANIFEST_URL): Promise<void> {
    return initialization ??= loadHostFontPacks(url);
}

async function loadHostFontPacks(url: string | undefined): Promise<void> {
    if (!url) return;

    const fail = (why: string): void => {
        console.warn(`[Fonts] font pack manifest ${url} ${why}; drawing the bundled fonts only.`);
    };
    const controller = new AbortController();
    const timer = setTimeout(() => {
        controller.abort(new Error(`no answer within ${FONT_MANIFEST_TIMEOUT_MS / 1000}s`));
    }, FONT_MANIFEST_TIMEOUT_MS);
    try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
            fail(`answered ${response.status}`);
            return;
        }
        const body: unknown = await response.json();
        if (!Array.isArray(body)) {
            fail('is not a JSON array');
            return;
        }
        const packs = body.filter(isHostedFontPack);
        if (packs.length < body.length) {
            console.warn(
                `[Fonts] ${body.length - packs.length} of ${body.length} entries in ${url} have no `
                + `"family" or "faces"; declaring the other ${packs.length}.`,
            );
        }
        if (packs.length === 0) return;
        declareFontPacks(packs);
        console.log(`[Fonts] declared ${packs.length} hosted font pack(s): ${packs.map(p => p.family).join(', ')}`);
    } catch (error) {
        fail(`failed to load (${String(error)})`);
    } finally {
        clearTimeout(timer);
    }
}
